/**
 * One level of the dictionary, as the shared tabs see it (I-3024). The hub
 * edits the session's own level through the public door (`useAccountConfig`),
 * the admin app a company's or tenant's level through master's admin door
 * (`useSubjectConfigs`); the tabs — corrections, never suggest, synonyms —
 * only know this adapter, so both mount the same components.
 */
import type { Ref } from 'vue';
import type {
  AccountConfigHandle,
  AccountConfigLayer,
  AccountConfigTarget,
  EnforcedField,
} from '../../config/accountConfig';
import type { DictionaryLevel } from '../../search/dictionaryApi';

/** How a write ended; `code` is master's problem code (`field_enforced`, `invalid_config`, `forbidden` …). */
export type DictionaryWriteResult =
  | { ok: true }
  | { ok: false; status: number; code: string; message: string };

/** The adapter. `T` is the document of the key the tab edits (`spell_check` or `search`). */
export interface DictionaryLevelHandle<T> {
  /** Which level is written: the wording of the lock and origin texts follows it. */
  key: DictionaryLevel;
  /** The document as a session of this level's subjects sees it: the layers before it with the own one over them. */
  effective: Readonly<Ref<T | undefined>>;
  /** Every layer of the key, to tell the own entries from the inherited ones; null while unknown. */
  layers: Readonly<Ref<AccountConfigLayer[] | null>>;
  /** The layer that is written. */
  target: Readonly<Ref<AccountConfigTarget | null>>;
  /** Top-level fields a layer BEFORE the written one locks. */
  enforced: Readonly<Ref<readonly EnforcedField[]>>;
  /** The key was read (the tab shows its list, not a spinner). */
  loaded: Readonly<Ref<boolean>>;
  /** Master refused a write for a missing permission: the tabs stay read-only. */
  forbidden?: Readonly<Ref<boolean>>;
  /** Merges one patch into the written layer, one answer for the whole patch. */
  patch(mergePatch: object): Promise<DictionaryWriteResult>;
  /** Top-level fields the written layer locks for every later layer (company and tenant only). */
  locks?: Readonly<Ref<readonly string[]>>;
  setLock?(field: string, locked: boolean): Promise<DictionaryWriteResult>;
}

/** The session's own level on the public door: a patch is sent at once and answers with master's. */
export function accountDictionaryLevel<T extends object>(
  handle: AccountConfigHandle<T>,
): DictionaryLevelHandle<T> {
  return {
    key: 'account',
    effective: handle.effective as Readonly<Ref<T | undefined>>,
    layers: handle.layers,
    target: handle.target,
    enforced: handle.enforced,
    loaded: handle.loaded,
    async patch(mergePatch) {
      const written = await handle.patch(mergePatch as Record<string, unknown>, { immediate: true });
      if (written !== null) return { ok: true };
      const cause = handle.error.value;
      return {
        ok: false,
        status: cause?.status ?? 0,
        code: cause?.code ?? '',
        message: cause?.message ?? '',
      };
    },
  };
}

/**
 * A refused write as the error the write reporter reads (axios-shaped, with
 * master's status and code), so `reportWriteFailure` shows master's words.
 */
export function dictionaryWriteFailure(result: Extract<DictionaryWriteResult, { ok: false }>): Error {
  return Object.assign(new Error(result.message || result.code || 'the write failed'), {
    response: {
      status: result.status,
      data: { code: result.code, message: result.message },
    },
  });
}

/**
 * The switch row of `search.learnCorrections` on top of the corrections tab —
 * the same field as the setting in the search section, so a tenant's or
 * company's lock shows here too. The hub brings it; the admin views do not.
 */
export interface LearnCorrectionsSwitch {
  /** The resolved setting (a lock of a farther layer included). */
  value: boolean;
  /** A tenant or company fixed the setting: the switch only shows it. */
  locked: boolean;
  /** The setting has not been read yet: the switch waits. */
  loading: boolean;
  /** Master did not answer for the setting: the switch offers a retry. */
  loadFailed: boolean;
  set(on: boolean): void | Promise<void>;
  retry(): void;
}
